import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogIn, Loader2, Eye, ShieldCheck, ArrowLeft, Sun, Moon, Monitor } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { LOGO } from "../lib/api";
import { ProgereDownloadAppButton } from "../components/ProgereDownloadAppButton";

function LoginThemeToggle() {
  const { mode, theme, setMode } = useTheme();
  const next = mode === "light" ? "dark" : mode === "dark" ? "system" : "light";
  const label = mode === "system" ? "Auto (système)" : theme === "dark" ? "Sombre" : "Clair";
  return (
    <button type="button" className="top-btn" data-testid="login-theme-toggle"
      onClick={() => setMode(next)} title={`Apparence : ${label}`}
      style={{ position: "fixed", top: 18, right: 18, zIndex: 50 }}>
      {mode === "system" ? <Monitor size={18} /> : theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
    </button>
  );
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const [noAccount, setNoAccount] = useState(false);
  const [twofa, setTwofa] = useState(null); // {challenge_id, email_masked}
  const [acct, setAcct] = useState(null); // {email_masked} — code comptable requis
  const [acctCode, setAcctCode] = useState("");
  const [code, setCode] = useState("");
  const { login, verify2fa } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();

  const submit = async (e, em, pw) => {
    e?.preventDefault();
    setBusy(true); setNoAccount(false);
    try {
      const data = await login(em || email, pw || pwd);
      if (data?.twofa_required) {
        setTwofa({ challenge_id: data.challenge_id, email_masked: data.email_masked });
        toast.info("Code de vérification envoyé", { description: `Consultez votre courriel ${data.email_masked}` });
        return;
      }
      if (data?.accountant_code_required) {
        setAcct({ email_masked: data.email_masked });
        toast.info("Code comptable requis", { description: "Saisissez le code fourni par l'administrateur." });
        return;
      }
      nav(data?.user?.technician ? "/maintenance" : "/tableau");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) setNoAccount(true);
      toast.error("Connexion échouée", { description: err?.response?.data?.detail || "Vérifiez vos identifiants" });
    } finally { setBusy(false); }
  };

  const submitAcctCode = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const data = await login(email, pwd, acctCode.trim());
      if (data?.twofa_required) {
        setAcct(null);
        setTwofa({ challenge_id: data.challenge_id, email_masked: data.email_masked });
        return;
      }
      nav("/tableau");
    } catch (err) {
      toast.error("Code comptable invalide", { description: err?.response?.data?.detail || "Vérifiez le code fourni par l'administrateur." });
    } finally { setBusy(false); }
  };

  const submitCode = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const data = await verify2fa(twofa.challenge_id, code.trim());
      nav(data?.user?.technician ? "/maintenance" : "/tableau");
    } catch (err) {
      toast.error("Vérification échouée", { description: err?.response?.data?.detail || "Code invalide" });
    } finally { setBusy(false); }
  };

  if (acct) {
    return (
      <div className="login-wrap">
        <div className="login-card fade-in">
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
            <div className="mc-ico"><ShieldCheck size={26} /></div>
          </div>
          <h1 style={{ textAlign: "center", fontSize: 22, margin: "0 0 6px" }}>Code comptable</h1>
          <p style={{ textAlign: "center", color: "var(--muted)", fontSize: 13, margin: "0 0 22px" }}>
            Ce compte comptable nécessite le code d'accès fourni par l'administrateur de l'entreprise.
          </p>
          <form onSubmit={submitAcctCode}>
            <label className="field-label">Code d'accès comptable</label>
            <input className="input" style={{ width: "100%", marginBottom: 18, textAlign: "center", letterSpacing: 3, fontSize: 18, fontFamily: "monospace", textTransform: "uppercase" }}
              value={acctCode} autoFocus onChange={(e) => setAcctCode(e.target.value.toUpperCase())}
              placeholder="COMPTA-XXXXXX" required data-testid="accountant-code-input" />
            <button className="btn btn-gold" style={{ width: "100%" }} disabled={busy || !acctCode.trim()} data-testid="accountant-code-submit">
              {busy ? <Loader2 size={17} className="spin" /> : <ShieldCheck size={17} />} Valider le code
            </button>
          </form>
          <button type="button" className="btn btn-outline btn-sm" style={{ width: "100%", marginTop: 14 }}
            onClick={() => { setAcct(null); setAcctCode(""); }} data-testid="accountant-code-back">
            <ArrowLeft size={15} /> {t("common.back")}
          </button>
        </div>
      </div>
    );
  }

  if (twofa) {
    return (
      <div className="login-wrap">
        <div className="login-card fade-in">
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
            <div className="mc-ico"><ShieldCheck size={26} /></div>
          </div>
          <h1 style={{ textAlign: "center", fontSize: 22, margin: "0 0 6px" }}>{t("login.codeSent")}</h1>
          <p style={{ textAlign: "center", color: "var(--muted)", fontSize: 13, margin: "0 0 22px" }}>
            <strong>{twofa.email_masked}</strong>.
          </p>
          <form onSubmit={submitCode}>
            <label className="field-label">{t("login.verifyCode")}</label>
            <input className="input" style={{ width: "100%", marginBottom: 18, textAlign: "center", letterSpacing: 8, fontSize: 22, fontFamily: "monospace" }}
              inputMode="numeric" maxLength={6} value={code} autoFocus
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="••••••" required data-testid="twofa-code-input" />
            <button className="btn btn-gold" style={{ width: "100%" }} disabled={busy || code.length < 6} data-testid="twofa-submit">
              {busy ? <Loader2 size={17} className="spin" /> : <ShieldCheck size={17} />} {t("login.verify")}
            </button>
          </form>
          <button type="button" className="btn btn-outline btn-sm" style={{ width: "100%", marginTop: 14 }}
            onClick={() => { setTwofa(null); setCode(""); }} data-testid="twofa-back">
            <ArrowLeft size={15} /> {t("common.back")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-wrap">
      <LoginThemeToggle />
      <div className="login-card fade-in">
        <img src={LOGO} alt="" referrerPolicy="no-referrer" className="login-logo" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        <h1 className="wordmark-lg" style={{ textAlign: "center", margin: "0 0 4px" }}>
          <span className="pro">PRO</span><span className="gere">GÈRE</span> <span className="punch">PUNCH</span>
        </h1>
        <p style={{ textAlign: "center", color: "var(--muted)", fontSize: 13, margin: "0 0 24px" }}>
          {t("login.tagline")}
        </p>
        {noAccount && (
          <div className="card gold-border" style={{ marginBottom: 16, padding: 14 }} data-testid="no-account-cta">
            <p style={{ margin: "0 0 10px", fontSize: 13, color: "var(--text)" }}>Pas encore de compte ? Découvrez nos forfaits et créez votre espace en quelques minutes.</p>
            <button type="button" className="btn btn-gold btn-sm" style={{ width: "100%" }} onClick={() => nav("/?forfaits=1")} data-testid="goto-forfaits-btn">Voir les forfaits</button>
          </div>
        )}
        <form onSubmit={submit}>
          <label className="field-label">{t("login.email")}</label>
          <input className="input" style={{ width: "100%", marginBottom: 14 }} type="email" value={email}
            onChange={(e) => setEmail(e.target.value)} placeholder="vous@entreprise.ca" required data-testid="login-email" />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <label className="field-label">{t("login.password")}</label>
            <button type="button" onClick={() => nav("/mot-de-passe-oublie")} data-testid="forgot-password-link"
              style={{ background: "none", border: "none", color: "var(--gold)", fontSize: 12, cursor: "pointer", fontFamily: "inherit", padding: 0 }}>
              {t("login.forgot")}
            </button>
          </div>
          <input className="input" style={{ width: "100%", marginBottom: 20 }} type="password" value={pwd}
            onChange={(e) => setPwd(e.target.value)} placeholder="••••••••" required data-testid="login-password" />
          <button className="btn btn-gold" style={{ width: "100%" }} disabled={busy} data-testid="login-submit">
            {busy ? <Loader2 size={17} className="spin" /> : <LogIn size={17} />} {t("login.signIn")}
          </button>
        </form>
        <div style={{ marginTop: 22, paddingTop: 18, borderTop: "1px solid var(--border-soft)", textAlign: "center" }}>
          <button type="button" className="btn btn-outline" style={{ width: "100%" }} disabled={busy}
            onClick={(e) => { localStorage.setItem("pp_demo", "1"); submit(e, "demo@progere.ca", "Demo123!"); }} data-testid="demo-login-btn">
            <Eye size={16} /> {t("login.demo")}
          </button>
          <p style={{ color: "var(--muted)", fontSize: 12, margin: "10px 0 0" }}>
            {t("login.demoSub")}
          </p>
        </div>
        <div style={{ marginTop: 16 }}>
          <ProgereDownloadAppButton variant="login" size="md" moduleCode="punch" />
        </div>
        <p style={{ color: "var(--muted)", fontSize: 11, textAlign: "center", marginTop: 18 }} data-testid="login-brand-credit">
          PROGÈRE — {t("login.poweredBy")} <strong style={{ color: "var(--gold)" }}>Groupe MJR Construction Inc.</strong>
        </p>
      </div>
    </div>
  );
}
